import React from "react";
import { PlatformIcon } from "./PlatformIcon";
import {
  Wallet, ArrowUpRight, Check, Lock, Sparkles, TrendingUp,
  CreditCard, Calendar, Brain, ShieldCheck, Wifi, BatteryFull,
} from "lucide-react";

/* -------- Phone Frame -------- */
export const PhoneFrame = ({ children, label, testid }) => (
  <div className="shrink-0 w-[300px] snap-center" data-testid={testid}>
    <div className="text-center mb-4 font-mono-c text-[10px] uppercase tracking-[0.25em] text-[#00FF88]">
      {label}
    </div>
    <div className="relative rounded-[44px] p-[6px] bg-gradient-to-br from-white/15 to-white/[0.02] shadow-[0_40px_80px_-30px_rgba(0,255,136,0.35)]">
      <div className="rounded-[40px] overflow-hidden bg-[#050505] border border-white/10 h-[600px] relative">
        {/* status bar */}
        <div className="absolute top-0 inset-x-0 z-20 flex items-center justify-between px-6 pt-3 text-[10px] font-mono-c text-white/80">
          <span>9:41</span>
          <div className="flex items-center gap-1.5">
            <Wifi className="w-3 h-3" />
            <BatteryFull className="w-3.5 h-3.5" />
          </div>
        </div>
        {/* notch */}
        <div className="absolute top-2 left-1/2 -translate-x-1/2 w-24 h-6 bg-black rounded-full z-30" />
        <div className="absolute inset-0 pt-10">{children}</div>
      </div>
    </div>
  </div>
);

/* -------- Reusable inner mini chart -------- */
const MiniChart = ({ color = "#00FF88" }) => (
  <svg viewBox="0 0 240 80" className="w-full h-16">
    <defs>
      <linearGradient id="m1" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={color} stopOpacity="0.5" />
        <stop offset="100%" stopColor={color} stopOpacity="0" />
      </linearGradient>
    </defs>
    <path d="M0,60 C30,50 50,30 80,35 C110,40 130,60 160,45 C190,30 210,15 240,10 L240,80 L0,80 Z" fill="url(#m1)" />
    <path d="M0,60 C30,50 50,30 80,35 C110,40 130,60 160,45 C190,30 210,15 240,10" stroke={color} strokeWidth="2" fill="none" />
  </svg>
);

/* ============ Screens ============ */

const ScreenOnboarding = () => (
  <div className="h-full flex flex-col justify-between p-6 text-center">
    <div />
    <div>
      <div className="mx-auto w-20 h-20 rounded-3xl bg-[#00FF88]/10 border border-[#00FF88]/20 grid place-items-center mb-6">
        <Sparkles className="w-9 h-9 text-[#00FF88]" />
      </div>
      <h3 className="font-display text-2xl font-extrabold leading-tight">
        Welcome to <span className="text-[#00FF88]">GigAfloat</span>
      </h3>
      <p className="text-white/55 text-sm mt-3 leading-relaxed">
        Connect your gigs. See every dollar.
        <br />Stay afloat between payouts.
      </p>
    </div>
    <div className="space-y-3">
      <button className="w-full h-12 bg-[#00FF88] text-black font-bold rounded-full btn-glow">
        Get started
      </button>
      <button className="w-full h-12 border border-white/15 rounded-full text-white/80 font-semibold text-sm">
        I already have an account
      </button>
      <p className="text-[10px] text-white/30 font-mono-c flex items-center justify-center gap-1.5 pt-1">
        <Lock className="w-3 h-3" /> Bank-grade · 256-bit · No credit check
      </p>
    </div>
  </div>
);

const ScreenConnect = () => {
  const platforms = [
    { id: "doordash", connected: true },
    { id: "uber", connected: true },
    { id: "lyft", connected: false },
    { id: "instacart", connected: false },
    { id: "spark", connected: false },
    { id: "roadie", connected: true },
  ];
  return (
    <div className="h-full flex flex-col p-5">
      <div className="mb-4">
        <div className="font-mono-c text-[9px] uppercase tracking-widest text-[#00FF88]">Step 2 of 3</div>
        <h3 className="font-display text-xl font-extrabold mt-1 leading-tight">Connect your gig platforms</h3>
        <p className="text-white/50 text-xs mt-1">Pick the platforms you earn on.</p>
      </div>
      <div className="flex-1 space-y-2 overflow-hidden">
        {platforms.map((p) => (
          <div key={p.id} className="flex items-center justify-between p-3 rounded-2xl bg-white/[0.03] border border-white/5">
            <div className="flex items-center gap-3">
              <PlatformIcon id={p.id} size={36} />
              <div>
                <div className="font-semibold text-sm capitalize">{p.id}</div>
                <div className="text-[10px] text-white/40 font-mono-c">{p.connected ? "Synced · 2 min ago" : "Read-only access"}</div>
              </div>
            </div>
            {p.connected ? (
              <div className="w-8 h-8 rounded-full bg-[#00FF88] grid place-items-center">
                <Check className="w-4 h-4 text-black" />
              </div>
            ) : (
              <button className="text-xs font-bold text-[#00FF88] border border-[#00FF88]/30 rounded-full px-3 py-1.5">Connect</button>
            )}
          </div>
        ))}
      </div>
      <button className="mt-4 w-full h-11 bg-[#00FF88] text-black font-bold rounded-full text-sm btn-glow">Continue →</button>
    </div>
  );
};

const ScreenDashboard = () => (
  <div className="h-full flex flex-col p-5 overflow-hidden">
    <div className="flex items-center justify-between mb-4">
      <div>
        <div className="text-[10px] text-white/40 font-mono-c uppercase tracking-widest">Good evening, Alex</div>
        <h3 className="font-display font-extrabold text-lg">Income overview</h3>
      </div>
      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#00FF88] to-[#14F195]" />
    </div>
    <div className="rounded-2xl p-4 bg-gradient-to-br from-[#00FF88]/15 to-transparent border border-[#00FF88]/20 mb-3">
      <div className="text-[10px] text-white/50 font-mono-c uppercase tracking-widest">This week</div>
      <div className="font-display text-3xl font-extrabold mt-1">$1,284.30</div>
      <div className="text-xs text-[#00FF88] font-semibold mt-1 flex items-center gap-1">
        <TrendingUp className="w-3 h-3" /> +18% vs avg
      </div>
      <MiniChart />
    </div>
    <div className="space-y-2 overflow-hidden">
      {[
        { id: "doordash", label: "DoorDash · 22 deliveries", amt: 286.2 },
        { id: "uber", label: "Uber · 14 trips", amt: 412.5 },
        { id: "instacart", label: "Instacart · 8 batches", amt: 100.6 },
        { id: "spark", label: "Spark · 6 routes", amt: 185.0 },
      ].map((r) => (
        <div key={r.id} className="flex items-center gap-3 p-2.5 rounded-xl bg-white/[0.03] border border-white/5">
          <PlatformIcon id={r.id} size={30} />
          <div className="flex-1">
            <div className="text-xs font-semibold text-white">{r.label}</div>
            <div className="text-[10px] text-white/40 font-mono-c">deposited today</div>
          </div>
          <div className="text-sm font-mono-c font-bold text-[#00FF88]">+${r.amt}</div>
        </div>
      ))}
    </div>
  </div>
);

const ScreenAdvance = () => (
  <div className="h-full flex flex-col p-5">
    <div className="font-mono-c text-[9px] uppercase tracking-widest text-[#00FF88]">Smart advance</div>
    <h3 className="font-display text-xl font-extrabold mt-1">You're eligible 🚀</h3>
    <div className="mt-5 flex-1 relative rounded-3xl bg-gradient-to-br from-[#00FF88]/10 via-transparent to-transparent border border-[#00FF88]/30 p-5 edge-glow">
      <CreditCard className="w-7 h-7 text-[#00FF88] mb-3" />
      <div className="text-[10px] text-white/50 font-mono-c uppercase tracking-widest">Up to</div>
      <div className="font-display text-5xl font-black mt-1 leading-none">
        $<span className="bg-gradient-to-br from-[#00FF88] to-[#14F195] bg-clip-text text-transparent">480</span>
      </div>
      <div className="text-xs text-white/55 mt-2">Repay by Fri, Nov 28 · Flat $4 fee</div>

      <div className="mt-6 space-y-2.5">
        {["Based on $1,284 weekly avg", "No credit pull", "Auto-deducts when you get paid"].map((t) => (
          <div key={t} className="flex items-center gap-2 text-xs text-white/70">
            <div className="w-4 h-4 rounded-full bg-[#00FF88]/20 grid place-items-center">
              <Check className="w-2.5 h-2.5 text-[#00FF88]" />
            </div>
            {t}
          </div>
        ))}
      </div>

      <input type="range" min="50" max="480" defaultValue="200" className="w-full mt-6 accent-[#00FF88]" />
      <div className="flex justify-between text-[10px] font-mono-c text-white/40 mt-1">
        <span>$50</span><span className="text-[#00FF88] font-bold">$200 selected</span><span>$480</span>
      </div>
    </div>
    <button className="mt-4 w-full h-12 bg-[#00FF88] text-black font-bold rounded-full btn-glow">Get $200 now</button>
  </div>
);

const ScreenRepay = () => (
  <div className="h-full flex flex-col p-5">
    <div className="font-mono-c text-[9px] uppercase tracking-widest text-[#00FF88]">Repayment</div>
    <h3 className="font-display text-xl font-extrabold mt-1">On track 📈</h3>

    <div className="mt-5 rounded-2xl bg-white/[0.03] border border-white/5 p-4">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs text-white/60">$200 advance</span>
        <span className="text-xs text-[#00FF88] font-mono-c font-bold">75% paid</span>
      </div>
      <div className="h-2 rounded-full bg-white/5 overflow-hidden">
        <div className="h-full w-3/4 bg-gradient-to-r from-[#00FF88] to-[#14F195] rounded-full" />
      </div>
      <div className="mt-3 font-display font-extrabold text-2xl">$50.00 <span className="text-white/40 text-sm font-mono-c font-normal">left</span></div>
    </div>

    <div className="mt-4 text-[10px] uppercase tracking-widest font-mono-c text-white/40">Auto-debits</div>
    <div className="mt-2 space-y-2 flex-1">
      {[
        { d: "Mon, Nov 20", amt: 50, paid: true, src: "Uber payout" },
        { d: "Wed, Nov 22", amt: 50, paid: true, src: "DoorDash payout" },
        { d: "Fri, Nov 24", amt: 50, paid: true, src: "Roadie payout" },
        { d: "Mon, Nov 27", amt: 50, paid: false, src: "Upcoming · Uber" },
      ].map((r, i) => (
        <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/5">
          <div className={`w-8 h-8 rounded-full grid place-items-center ${r.paid ? "bg-[#00FF88]" : "bg-white/5 border border-white/10"}`}>
            {r.paid ? <Check className="w-4 h-4 text-black" /> : <Calendar className="w-3.5 h-3.5 text-white/40" />}
          </div>
          <div className="flex-1">
            <div className="text-xs font-semibold">{r.d}</div>
            <div className="text-[10px] text-white/40 font-mono-c">{r.src}</div>
          </div>
          <div className={`text-sm font-mono-c font-bold ${r.paid ? "text-white/60" : "text-[#00FF88]"}`}>${r.amt}</div>
        </div>
      ))}
    </div>
  </div>
);

const ScreenSpending = () => (
  <div className="h-full flex flex-col p-5">
    <div className="font-mono-c text-[9px] uppercase tracking-widest text-[#00FF88]">Spending insights</div>
    <h3 className="font-display text-xl font-extrabold mt-1">Where it went</h3>

    <div className="mt-5 relative h-44 grid place-items-center">
      {/* Donut */}
      <svg viewBox="0 0 100 100" className="w-44 h-44 -rotate-90">
        <circle cx="50" cy="50" r="40" stroke="rgba(255,255,255,0.06)" strokeWidth="12" fill="none" />
        <circle cx="50" cy="50" r="40" stroke="#00FF88" strokeWidth="12" fill="none" strokeDasharray="251" strokeDashoffset="100" />
        <circle cx="50" cy="50" r="40" stroke="#14F195" strokeWidth="12" fill="none" strokeDasharray="251" strokeDashoffset="180" />
        <circle cx="50" cy="50" r="40" stroke="#FFD60A" strokeWidth="12" fill="none" strokeDasharray="251" strokeDashoffset="225" />
      </svg>
      <div className="absolute text-center">
        <div className="text-[10px] uppercase font-mono-c text-white/40 tracking-widest">Total</div>
        <div className="font-display font-extrabold text-2xl">$842</div>
      </div>
    </div>

    <div className="mt-4 space-y-2">
      {[
        { c: "#00FF88", label: "Gas & vehicle", v: "$324" },
        { c: "#14F195", label: "Food", v: "$268" },
        { c: "#FFD60A", label: "Phone & data", v: "$98" },
        { c: "rgba(255,255,255,0.3)", label: "Other", v: "$152" },
      ].map((r) => (
        <div key={r.label} className="flex items-center gap-3 p-2.5 rounded-xl bg-white/[0.03] border border-white/5">
          <div className="w-2.5 h-2.5 rounded-full" style={{ background: r.c }} />
          <div className="flex-1 text-xs">{r.label}</div>
          <div className="text-xs font-mono-c font-bold">{r.v}</div>
        </div>
      ))}
    </div>
  </div>
);

const ScreenPrediction = () => (
  <div className="h-full flex flex-col p-5">
    <div className="font-mono-c text-[9px] uppercase tracking-widest text-[#00FF88]">Cash flow forecast</div>
    <h3 className="font-display text-xl font-extrabold mt-1">Next 14 days</h3>

    <div className="mt-5 rounded-2xl bg-white/[0.03] border border-white/5 p-4">
      <div className="flex items-end justify-between mb-3">
        <div>
          <div className="text-[10px] text-white/40 font-mono-c uppercase tracking-widest">Projected</div>
          <div className="font-display text-2xl font-extrabold">$2,418</div>
        </div>
        <div className="text-xs text-[#00FF88] font-mono-c font-bold">94% confidence</div>
      </div>
      <svg viewBox="0 0 240 90" className="w-full h-24">
        <defs>
          <linearGradient id="pred" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#00FF88" stopOpacity="0.45" />
            <stop offset="100%" stopColor="#00FF88" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d="M0,70 C30,60 50,40 80,50 C110,60 130,30 160,35 L160,90 L0,90 Z" fill="url(#pred)" />
        <path d="M0,70 C30,60 50,40 80,50 C110,60 130,30 160,35" stroke="#00FF88" strokeWidth="2" fill="none" />
        <path d="M160,35 C180,30 200,15 240,8" stroke="#00FF88" strokeWidth="2" strokeDasharray="4 4" fill="none" />
        <circle cx="160" cy="35" r="3" fill="#00FF88" />
      </svg>
    </div>

    <div className="mt-4 rounded-2xl p-4 bg-gradient-to-br from-[#00FF88]/10 to-transparent border border-[#00FF88]/20">
      <Brain className="w-5 h-5 text-[#00FF88] mb-2" />
      <div className="text-xs text-white/70 leading-relaxed">
        <span className="font-bold text-white">Heads up:</span> slow week predicted Nov 28–Dec 4.
        Set aside <span className="text-[#00FF88] font-bold font-mono-c">$180</span> from this week to coast.
      </div>
      <button className="mt-3 text-xs font-bold text-[#00FF88] flex items-center gap-1 hover:gap-2 transition-all">
        Set aside automatically <ArrowUpRight className="w-3.5 h-3.5" />
      </button>
    </div>
    <div className="flex-1" />
  </div>
);

const ScreenWellness = () => (
  <div className="h-full flex flex-col p-5">
    <div className="font-mono-c text-[9px] uppercase tracking-widest text-[#00FF88]">Financial wellness</div>
    <h3 className="font-display text-xl font-extrabold mt-1">Your money, calm.</h3>

    <div className="mt-5 grid grid-cols-2 gap-3">
      <div className="rounded-2xl p-4 bg-white/[0.03] border border-white/5">
        <Wallet className="w-5 h-5 text-[#00FF88] mb-2" />
        <div className="text-[10px] uppercase tracking-widest font-mono-c text-white/40">Tax vault</div>
        <div className="font-display font-extrabold text-xl mt-0.5">$428</div>
      </div>
      <div className="rounded-2xl p-4 bg-white/[0.03] border border-white/5">
        <ShieldCheck className="w-5 h-5 text-[#00FF88] mb-2" />
        <div className="text-[10px] uppercase tracking-widest font-mono-c text-white/40">Safety net</div>
        <div className="font-display font-extrabold text-xl mt-0.5">$1,205</div>
      </div>
    </div>

    <div className="mt-3 rounded-2xl p-4 bg-gradient-to-br from-[#00FF88]/10 to-transparent border border-[#00FF88]/20 edge-glow">
      <div className="text-[10px] uppercase tracking-widest font-mono-c text-[#00FF88]">Goal · Emergency fund</div>
      <div className="flex items-baseline gap-1 mt-1">
        <span className="font-display font-extrabold text-2xl">$1,205</span>
        <span className="text-white/40 text-xs font-mono-c">/ $3,000</span>
      </div>
      <div className="mt-3 h-2 rounded-full bg-white/10 overflow-hidden">
        <div className="h-full w-[40%] bg-gradient-to-r from-[#00FF88] to-[#14F195]" />
      </div>
      <div className="mt-2 text-[10px] text-white/50 font-mono-c">+$24 auto-saved this week</div>
    </div>

    <div className="flex-1" />
    <button className="w-full h-12 bg-[#00FF88] text-black font-bold rounded-full btn-glow">Open a new vault</button>
  </div>
);

const screens = [
  { id: "onboarding", label: "01 · Onboarding", el: <ScreenOnboarding /> },
  { id: "connect", label: "02 · Connect gigs", el: <ScreenConnect /> },
  { id: "dashboard", label: "03 · Dashboard", el: <ScreenDashboard /> },
  { id: "advance", label: "04 · Advance", el: <ScreenAdvance /> },
  { id: "repay", label: "05 · Repayment", el: <ScreenRepay /> },
  { id: "spending", label: "06 · Spending", el: <ScreenSpending /> },
  { id: "prediction", label: "07 · Forecast", el: <ScreenPrediction /> },
  { id: "wellness", label: "08 · Wellness", el: <ScreenWellness /> },
];

const MobileShowcase = () => {
  return (
    <section id="app" className="relative py-24 md:py-32 border-t border-white/5" data-testid="mobile-showcase">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 mb-14">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div className="max-w-2xl">
            <div className="font-mono-c text-[10px] uppercase tracking-[0.25em] text-[#00FF88] mb-4">
              // The app
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.02] tracking-tighter">
              A pocket co-pilot
              <br />
              <span className="text-white/40">for your earnings.</span>
            </h2>
          </div>
          <p className="text-white/55 max-w-md text-base leading-relaxed">
            Swipe through the GigAfloat experience. Onboarding to forecast,
            crafted with the precision of a fintech, the warmth of a movement.
          </p>
        </div>
      </div>

      <div
        className="overflow-x-auto no-scrollbar snap-x-mandatory px-6 lg:px-12 pb-10"
        data-testid="phone-gallery"
      >
        <div className="flex gap-8 w-max">
          {screens.map((s) => (
            <PhoneFrame key={s.id} label={s.label} testid={`phone-${s.id}`}>
              {s.el}
            </PhoneFrame>
          ))}
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 text-center">
        <p className="text-xs font-mono-c text-white/40 uppercase tracking-[0.25em]">
          ← Scroll to explore all 9 screens →
        </p>
      </div>
    </section>
  );
};

export default MobileShowcase;
